import { XMLParser, XMLBuilder } from "fast-xml-parser";
import { isValidXml } from "../Reading/StateManager/util";
import { TableBlock, TableCell } from "../Reading/types";

const ATTR_XMLNS = "xmlns";
const VALUE_XMLNS = "http://innodatalabs.com/innodom";

// Common options for parsing XML while preserving structure and attributes.
const PARSER_OPTIONS = {
  ignoreAttributes: false,
  attributeNamePrefix: "@_",
  textNodeName: "#text",
  cdataPropName: "__cdata", // To handle CDATA content
  preserveOrder: true,     // CRITICAL: Ensures mixed content (like <p>text <b>bold</b> text</p>) is ordered correctly
  trimValues: false,
  unpairedTags: ["br"],
  parseTagValue: false, // will cause numeric or boolean values to be parsed as string rather than their object representation
  parseAttributeValue: false
};

const escapeXmlEntities = (input: string): string => {
  return input
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}


/**
 * Recursively traverses the JSON object created by fast-xml-parser (with preserveOrder: true)
 * and converts it into an HTML string.
 * @param node A node from the parsed JSON array.
 * @returns An HTML string representation of the node.
 */
const mapJsonNodeToHtml = (node: any): string => {
  if (node["#text"]) {
    return escapeXmlEntities(node["#text"]);
  }
  if (node["__cdata"]) {
    // fast-xml-parser may wrap CDATA in an array
    const cdataContent = Array.isArray(node["__cdata"]) ? node["__cdata"][0] : node["__cdata"];
    return escapeXmlEntities(cdataContent);
  }

  const tagName = Object.keys(node).find(key => key !== "@_");
  if (!tagName) return "";

  const children = node[tagName];
  const attributes = node[":@"] ?? {};

  const attrString = Object.keys(attributes)
    .map(key => ` ${key.replace("@_", "")}="${String(attributes[key]).replace(/\"/g, '\\"')}"`)
    .join("");
  if (!children || children.length === 0) {
    return `<${tagName}${attrString} />`;
  }

  const contents = children.map(mapJsonNodeToHtml).join("");
  return `<${tagName}${attrString}>${contents}</${tagName}>`;
};


export const convertInnoTextToReadingHTML = (input: string): string => {
  try {
    const parser = new XMLParser(PARSER_OPTIONS);
    const jsonObj = parser.parse(input);
    // With preserveOrder, the root is the first element of an array.
    if (!jsonObj[0] || !jsonObj[0].text) {
      throw new Error("Invalid Innodom. Missing root <text> element.");
    }

    // The children of <text> are in the `text` property array.
    return jsonObj[0].text
      .map(mapJsonNodeToHtml)
      .join("")
      .trim();
  } catch (err) {
    console.error(err);
    throw err;
  }
};

/**
 * Best-effort recovery for when strict XML parsing of a block fails — e.g. older
 * jobs whose reading-reviewed-json still contains a malformed font attribute
 * (font-name="...",+Italic"). Instead of letting fast-xml-parser throw and crash the
 * whole workbench load, we strip the outer <text> wrapper and hand the inner markup to
 * the lenient HTML pipeline (renderer + editor), which tolerates it. When the user
 * re-saves the block, the html-to-draftjs sanitization rewrites it as well-formed XML.
 */
export const recoverInnoTextLeniently = (input: string): string => {
  if (typeof input !== "string") {
    return "";
  }
  return input
    .replace(/^\s*<text\b[^>]*>/i, "")
    .replace(/<\/text>\s*$/i, "")
    .trim();
};

export const convertReadingHTMLToInnodom = (input: string): string => {
  // Using a template literal is simple and effective for wrapping content.
  // This avoids browser-specific APIs and is more performant than a full parse/build cycle.
  return `<text ${ATTR_XMLNS}="${VALUE_XMLNS}">${input}</text>`;
};

export const convertReadingTableBlockToInnodom = (input: TableBlock): string => {
  try {
    const rows = input.cells
      .map(row => {
        const cells = row
          .map(cell => {
            const attrs = Object.entries(cell.attributes || {})
              .map(([k, v]) => `${k}="${v}"`)
              .join(" ");
            return `<td ${attrs}>${cell.text}</td>`;
          })
          .join("");
        return `<tr>${cells}</tr>`;
      })
      .join("");
    return `<table xmlns="${VALUE_XMLNS}">${rows}</table>`;

  } catch (err) {
    console.error(err);
    throw new Error("Invalid XML generated.");
  }
};

const extractSpan = (attr: string) => (colAttributes: { [key: string]: string }): number => {
  if (!colAttributes) return 1;

  for (const key of Object.keys(colAttributes)) {
    const cleanKey = key.trim().toLowerCase();
    if (cleanKey === attr) {
      const parsedSpan = parseInt(colAttributes[key], 10);
      return !isNaN(parsedSpan) && parsedSpan > 0 ? parsedSpan : 1;
    }
  }
  return 1;
};
const extractColSpan = extractSpan("colspan");
const extractRowSpan = extractSpan("rowspan");

// DO NOT USE except for very old jobs, does not work in all cases since moving to fast-xml-parser
export const extractTableCells = (
  tableXml: string | undefined,
  blockId: string,
): TableBlock["cells"] => {
  if (typeof tableXml !== "string") {
    throw new Error("Missing table xml.");
  }
  const parser = new XMLParser(PARSER_OPTIONS);
  const jsonObj = parser.parse(tableXml);

  // With preserveOrder, root is jsonObj[0]
  const tableNode = jsonObj[0]?.table;
  if (!tableNode) return [];

  // This builder is used to reconstruct the inner content of a cell
  const contentBuilder = new XMLBuilder({
    ...PARSER_OPTIONS,
    format: false,
    suppressBooleanAttributes: false,
    suppressEmptyNode: false,
  });

  // `tr` nodes will be under the `table` property
  const rows = tableNode.filter((n: any) => n.tr) as any[];

  return rows.map((rowNode: any, rowIndex: number): Array<TableCell> => {
    // `td` nodes will be under the `tr` property
    const cols = rowNode.tr.filter((n: any) => n.td) as any[];
    return cols.map((colNode: any, colIndex: number): TableCell => {
        const attributes = colNode["@_"] ?? {};

        // Rebuild the inner XML/HTML content from the cell's child nodes
        let text = colNode.td ? contentBuilder.build(colNode.td) : "";

        text = text.replace(/<br>/g, "<br/>");

        return {
          attributes,
          colSpan: extractColSpan(attributes),
          id: `${blockId}-row-${rowIndex + 1}-col-${colIndex + 1}`,
          rowSpan: extractRowSpan(attributes),
          text,
          validXml: isValidXml(text),
        };
    });
  });
};

interface CellStructure {
  "cell-bbox": number[];
  "content-bbox": number[];
  "cell-type" : string;
  colspan: number;
  rowspan: number;
  "text": string;
}
interface TableStructure {
  rows : CellStructure[][];
}

export const extractTableCells2 = (
  tableStructure: TableStructure,
  blockId: string,
): TableBlock["cells"] => {
  // Configuration for "Ordered" mode to handle mixed content correctly
  const options = {
    preserveOrder: true,
    ignoreAttributes: false,
    attributeNamePrefix: "",
    parseAttributeValue: false,
  };

  const parser = new XMLParser(options);
  const builder = new XMLBuilder(options);
  return tableStructure.rows.map((rowCells, rowIndex) : Array<TableCell> => {
    return rowCells.map((cell, colIndex) : TableCell => {

      let processedText = "";

      try {
        if (cell.text) {
          const parsed = parser.parse(cell.text);
          
          // In preserveOrder: true, 'parsed' is an array of objects.
          // We look for the root node (usually "text") and build its children.
          const rootNode = parsed.find((node: any) => node.text);
          
          if (rootNode && rootNode.text) {
            // Reconstruct only the inner contents of the <text> tag
            processedText = builder.build(rootNode.text);
          } else {
            // Fallback if the XML structure is unexpected but contains text
            processedText = cell.text;
          }
        }
      } catch (e) {
        console.error("Failed to parse cell XML:", e);
        processedText = cell.text; // Fallback to raw text on error
      }

      return {
        // Mapping new CellStructure properties to your TableCell interface
        attributes: { "cell-type": cell["cell-type"] }, 
        colSpan: cell.colspan,
        rowSpan: cell.rowspan,
        id: `${blockId}-row-${rowIndex + 1}-col-${colIndex + 1}`,
        text: processedText,
        validXml: isValidXml(processedText),
      };
    })
  })
}